import Vue from "vue";
import { $http } from "@/utils/https";
import { fixPage } from "@/helpers/variables";

let inflightHistory = null;
const inflightCourseBySlug = Object.create(null);
// monotonically increasing request token per slug: with force-requests two
// fetches for the same slug can overlap, and only the LATEST one may write
// to the store — otherwise a slow stale response overwrites fresh data
const courseRequestSeq = Object.create(null);
const TTL_MS = 60000;

import {
	CHANGE_CATEGORIES_LOADING,
	CHANGE_CATEGORY,
	CHANGE_SORT,
	CHANGE_LIST_LOADING,
	CHANGE_POPUP_TRAILER,
	CHANGE_SEARCH_LOADING,
	CHANGE_SKIP,
	COURSES_NEW_REQUEST_STATUS,
	COURSES_NEW_SET_LIST,
	GET_COURSE_ITEM_LOADING,
	GET_TIMETABLE_LOADING,
	SET_CATEGORIES,
	SET_COMPONENTS,
	SET_ITEM,
	SET_LIST,
	SET_LIST_LENGTH,
	SET_SEARCH,
	SET_TIMETABLE
} from "./types/mutation-types";

import {
	FETCH_NEW_COURSES,
	GET_CATEGORIES,
	GET_COMPONENTS,
	GET_COURSE_ITEM,
	GET_FILE,
	GET_LIST,
	GET_SEARCH,
	GET_SEARCH_HISTORY,
	GET_TIMETABLE,
	SET_SEARCH_HISTORY,
	UPDATE_VIDEO_SETTINGS
} from "./types/action-types";

const state = {
	newCourses			: null,
	coursesList			: [],
	currentItem			: null,
	_courseFetchedAt	: 0,
	isListLoading		: false,
	isLoading			: false,
	isLoadingNewCourses	: false,
	skip				: 0,
	limit				: 9,
	listLength			: 0,
	isLoadingList		: false,
	timetable			: null,
	timetableLoading	: false,
	showPopupTrailer	: false,
	
	categories			: [],
	isLoadingCategories	: false,
	activeCategory		: 1,
	selectedSort		: "sortByDate",
	components			: [],
	
	isSearchLoading		: false,
	searchList			: [],
	searchHistoryList	: [],
	
	savedVolume			: 1,
	savedTime			: 0,
	
	_isSearchHistoryLoading: false,
	_searchHistoryFetchedAt: 0
};

const getters = {
	coursesList			: (state) => state.coursesList,
	currentItem			: (state) => state.currentItem,
	listLength			: (state) => state.listLength,
	skip				: (state) => state.skip,
	isListLoading		: (state) => state.isListLoading,
	isLoading			: (state) => state.isLoading,
	
	newCourses			: (state) => state.newCourses,
	isLoadingNewCourses	: (state) => state.isLoadingNewCourses,
	
	timetable			: (state) => state.timetable,
	timetableLoading	: (state) => state.timetableLoading,
	showPopupTrailer	: (state) => state.showPopupTrailer,
	
	categories			: (state) => state.categories,
	isLoadingCategories	: (state) => state.isLoadingCategories,
	
	activeCategory		: (state) => state.activeCategory,
	components			: (state) => state.components,
	
	isSearchLoading		: (state) => state.isSearchLoading,
	searchList			: (state) => state.searchList,
	searchHistoryList	: (state) => state.searchHistoryList,
};

const actions = {
	[FETCH_NEW_COURSES]		: async ({ commit }) => {
		try {
			commit(COURSES_NEW_REQUEST_STATUS, true);
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}my/courses?include=course,course.image,course.image.seo`
			);
			
			commit(COURSES_NEW_SET_LIST, response.data.data);
			
			return response;
		} catch (e) {
			throw e;
		} finally {
			commit(COURSES_NEW_REQUEST_STATUS, false);
		}
	},
	[GET_LIST]				: async ({ commit }, payload) => {
		try {
			commit(CHANGE_LIST_LOADING, true);
			
			if (payload.isResetList) {
				commit(SET_LIST, []);
				commit(CHANGE_SKIP, true);
			} else if (!payload.isSkip) commit(CHANGE_SKIP);
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}my/courses?skip=${
					payload.isSkip ? 0 : state.skip
				}&limit=${
					state.limit
				}&include=course,course.image,course.image.seo,course.seo,course.category,course.rating,rating,currentMaterial.module,currentMaterial.lesson,currentMaterial.homeWork&filters[category]=${
					state.activeCategory
				}&sort=${state.selectedSort}`
			);
			
			payload.isResetList && commit(SET_LIST, []);
			
			if (payload.isSkip) {
				const payloadData = {
					data	: response.data.data,
					isSkip	: payload.isSkip
				};
				
				commit("SET_EMPTY_LIST", payloadData);
			} else {
				commit(SET_LIST, response.data.data);
				commit(SET_LIST_LENGTH, response.data.meta.total);
			}
			
			return response;
		} catch (e) {
			//if (payload.isResetList) {
				commit(CHANGE_LIST_LOADING, false);
			//}
			throw e;
		} finally {
			//if (payload.isResetList) {
				commit(CHANGE_LIST_LOADING, false);
			//}
		}
	},
	[GET_COURSE_ITEM]		: async ({ commit }, payload) => {
		const slug = payload && typeof payload === 'object' ? payload.slug : payload;
		const force = !!(payload && typeof payload === 'object' && payload.force);

		if (
			!force &&
			state.currentItem &&
			state.currentItem.slug === slug &&
			state._courseFetchedAt &&
			(Date.now() - state._courseFetchedAt < TTL_MS)
		) {
			return state.currentItem;
		}

		if (!force && inflightCourseBySlug[slug]) return inflightCourseBySlug[slug];

		const requestId = (courseRequestSeq[slug] = (courseRequestSeq[slug] || 0) + 1);

		commit(GET_COURSE_ITEM_LOADING, true);

		const request = $http
		.get(`${process.env.VUE_APP_ROOT_COURSE}courses/${slug}?include=category,type,image.seo,seo,header.images.seo,userCourse.rating,rating,modules.userModule,socials,modules.materials.image.seo,modules.materials.speakers,modules.materials.userLesson,modules.materials.userHomeWork,graduateWork.userGraduateWork,graduateWork.image,userCourse.currentMaterial.lesson,userCourse.currentMaterial.module,skills,skills.skill,category.translations,certificates,userCourse.userCertificate,userCourse.package&use_as_id=slug`)
		.then(({ data }) => {
			const payload = data && data.data ? data.data : null;

			// a newer request for this slug started meanwhile (e.g. a forced
			// refetch) — its response owns the store, do not overwrite it
			if (courseRequestSeq[slug] === requestId) {
				commit(SET_ITEM, payload);
				commit('SET_COURSE_FETCHED_AT', Date.now());
			}

			return payload;
		})
		.finally(() => {
			commit(GET_COURSE_ITEM_LOADING, false);

			if (inflightCourseBySlug[slug] === request) {
				delete inflightCourseBySlug[slug];
			}
		});

		inflightCourseBySlug[slug] = request;

		return request;
	},
	[GET_TIMETABLE]			: async ({ commit }, { slug, date }) => {
		try {
			commit(GET_TIMETABLE_LOADING, true);
			
			let dateIn = date ? `&filters[date_from]=${date}` : "";
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}courses/${slug}/timetable?include=userLesson,userHomeWork,userGraduateWork&use_as_id=slug${dateIn}`
			);
			
			commit(SET_TIMETABLE, response.data.data);
			
			return response;
		} catch (e) {
			throw e;
		} finally {
			commit(GET_TIMETABLE_LOADING, false);
		}
	},
	[GET_CATEGORIES]		: async ({ commit }) => {
		try {
			commit(CHANGE_CATEGORIES_LOADING, true);
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}categories`
			);
			
			commit(SET_CATEGORIES, response.data.data);
			
			return response;
		} catch (e) {
			commit(CHANGE_CATEGORIES_LOADING, false);
			throw e;
		} finally {
			commit(CHANGE_CATEGORIES_LOADING, false);
		}
	},
	[GET_COMPONENTS]		: async ({ commit }, { slug }) => {
		try {
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}courses/${slug}/components?use_as_id=slug`
			);
			
			commit(SET_COMPONENTS, response.data.data);
			
			return response;
		} catch (e) {
			throw e;
		}
	},
	[GET_FILE]				: async ({ commit }, payload) => {
		try {
			const fileResp = await $http.post(
				`${process.env.VUE_APP_ROOT_FILE}files`,
				payload
			);
			
			return fileResp;
		} catch (e) {
			throw e;
		} finally {
		}
	},
	[GET_SEARCH]			: async ({ commit }, payload) => {
		try {
			commit(CHANGE_SEARCH_LOADING, true);
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}courses/search?keywords=${payload}`
			);
			
			commit(SET_SEARCH, response.data.data);
			
			return response;
		} catch (e) {
			throw e;
		} finally {
			commit(CHANGE_SEARCH_LOADING, false);
		}
	},
	[GET_SEARCH_HISTORY]: async ({ state, commit }) => {
		const TTL_MS = 60000; // 60 c
		const fresh = state.searchHistoryList && state.searchHistoryList.length && (Date.now() - state._searchHistoryFetchedAt < TTL_MS);
		
		if (fresh) return state.searchHistoryList;
		
		if (inflightHistory) return inflightHistory;
		
		commit('SET_SEARCH_HISTORY_LOADING', true);
		
		inflightHistory = $http
		.get(`${process.env.VUE_APP_ROOT_COURSE}courses/search/history`)
		.then(({ data }) => {
			const items = (data && data.data) ? data.data : [];
			
			commit(SET_SEARCH_HISTORY, items);
			commit('SET_SEARCH_HISTORY_FETCHED_AT', Date.now());
			
			return items;
		})
		.finally(() => {
			commit('SET_SEARCH_HISTORY_LOADING', false);
			
			inflightHistory = null;
		});
		
		return inflightHistory;
	},
	[UPDATE_VIDEO_SETTINGS]	: async ({ commit }, payload) => {
		try {
			const response = await $http.post(
				`${process.env.VUE_APP_ROOT_COURSE}video`,
				payload
			);
			
			response;
			
			return;
		} catch (e) {
			throw e;
		} finally {
		}
	}
};

const mutations = {
	["SET_EMPTY_LIST"](state, props) {
		const { data, isSkip } = props;
		
		if (isSkip) {
			state.coursesList = data;
			state.skip = 0;
		}
	},
	[SET_LIST](state, data) {
		if (!data.length) return;
		//
		// state.coursesList = data;
		
		if (state.coursesList.length) {
			state.coursesList.push(...data);
		} else {
			state.coursesList = data;
		}
	},
	[SET_LIST_LENGTH](state, data) {
		state.listLength = data;
	},
	[CHANGE_SKIP](state, status) {
		if (status) {
			state.skip = 0;
			
			return;
		}
		
		state.skip += state.limit;
	},
	[SET_ITEM](state, data) {
		state.currentItem = data;
		
		if (state.currentItem) {
			state.currentItem.name = data.name;
			
			const item = state.currentItem.header.data;
			
			item.colorBackground = !item.colorBackground
				? "#f5f5f5ff"
				: item.colorBackground;
			item.colorTitle = !item.colorTitle ? "#000000FF" : item.colorTitle;
			item.colorDescription = !item.colorDescription
				? "#888888FF"
				: item.colorDescription;
			item.colorBreadcrumbs = !item.colorBreadcrumbs
				? "#888888FF"
				: item.colorBreadcrumbs;
			item.colorRatingActive = !item.colorRatingActive
				? "#b83f39FF"
				: item.colorRatingActive;
			item.colorRatingInactive = !item.colorRatingInactive
				? "#C4C4C4FF"
				: item.colorRatingInactive;
			item.colorButtonStartContinueStudying = !item.colorButtonStartContinueStudying
				? "#b83f39FF"
				: item.colorButtonStartContinueStudying;
			item.colorButtonStartContinueStudyingHover =
				!item.colorButtonStartContinueStudyingHover
					? "#ffffffff"
					: item.colorButtonStartContinueStudyingHover;
			item.colorButtonStartContinueStudyingText =
				!item.colorButtonStartContinueStudyingText
					? "#ffffffff"
					: item.colorButtonStartContinueStudyingText;
			item.colorButtonStartContinueStudyingTextHover =
				!item.colorButtonStartContinueStudyingTextHover
					? "#b83f39FF"
					: item.colorButtonStartContinueStudyingTextHover;
			item.colorButtonWatchTrailer = !item.colorButtonWatchTrailer
				? "#ffffffff"
				: item.colorButtonWatchTrailer;
			item.colorButtonWatchTrailerHover = !item.colorButtonWatchTrailerHover
				? "#000000FF"
				: item.colorButtonWatchTrailerHover;
			item.colorButtonWatchTrailerText = !item.colorButtonWatchTrailerText
				? "#000000FF"
				: item.colorButtonWatchTrailerText;
			item.colorButtonWatchTrailerTextHover = !item.colorButtonWatchTrailerTextHover
				? "#ffffffff"
				: item.colorButtonWatchTrailerTextHover;
			item.fontSize = !item.fontSize ? "medium" : item.fontSize;
		}
	},
	SET_COURSE_FETCHED_AT (s, ts) {
		s._courseFetchedAt = ts || Date.now();
	},
	// sync the just-submitted homework status straight from the complete()
	// response into the cached course tree, so the dashboard badge is correct
	// immediately — the backend queue recomputes aggregates with a lag, and a
	// plain refetch may still return the pre-recompute state
	UPDATE_HOMEWORK_STATUS(state, { slug, status }) {
		const course = state.currentItem;

		if (!course || !course.modules || !course.modules.data) return;

		course.modules.data.forEach((module) => {
			if (!module.materials || !module.materials.data) return;

			module.materials.data.forEach((material) => {
				if (
					material.slug === slug &&
					material.material !== 'lesson' &&
					material.userHomeWork &&
					material.userHomeWork.data &&
					material.userHomeWork.data.hasOwnProperty('id')
				) {
					Vue.set(material.userHomeWork.data, 'status', status);
				}
			});
		});
	},
	UPDATE_GRADUATE_WORK_STATUS(state, status) {
		const course = state.currentItem;

		if (
			course &&
			course.graduateWork &&
			course.graduateWork.data &&
			course.graduateWork.data.userGraduateWork &&
			course.graduateWork.data.userGraduateWork.data &&
			course.graduateWork.data.userGraduateWork.data.hasOwnProperty('id')
		) {
			Vue.set(course.graduateWork.data.userGraduateWork.data, 'status', status);
		}
	},
	[SET_TIMETABLE](state, data) {
		state.timetable = data;
	},
	[GET_TIMETABLE_LOADING](state, status) {
		state.timetableLoading = status;
	},
	[CHANGE_LIST_LOADING](state, status) {
		state.isListLoading = status;
	},
	[GET_COURSE_ITEM_LOADING](state, status) {
		state.isLoading = status;
	},
	[CHANGE_POPUP_TRAILER](state, status) {
		fixPage(status);
		state.showPopupTrailer = status;
	},
	[COURSES_NEW_SET_LIST](state, data) {
		state.newCourses = data;
	},
	[COURSES_NEW_REQUEST_STATUS](state, status) {
		state.isLoadingNewCourses = status;
	},
	[SET_CATEGORIES](state, data) {
		state.categories = data;
	},
	[CHANGE_CATEGORIES_LOADING](state, status) {
		state.isLoadingCategories = status;
	},
	[CHANGE_CATEGORY](state, category = 0) {
		if (category) {
			state.activeCategory = category;
		} else {
			state.activeCategory = 0;
		}
		
		state.skip = 0;
		state.listLength = 0;
		state.coursesList = [];
	},
	[CHANGE_SORT](state, sort = "sortByDate") {
		state.selectedSort = sort;
		state.skip = 0;
		state.listLength = 0;
		state.coursesList = [];
	},
	[SET_COMPONENTS](state, components) {
		state.components = components;
	},
	[CHANGE_SEARCH_LOADING](state, status) {
		state.isSearchLoading = status;
	},
	[SET_SEARCH](state, data) {
		if (!data.length) {
			state.searchList = {};
		} else {
			state.searchList = data;
		}
		//
	},
	[SET_SEARCH_HISTORY](state, data) {
		if (!data.length) {
			state.searchHistoryList = [];
		} else {
			state.searchHistoryList = data;
		}
		//
	},
	SET_SEARCH_HISTORY_LOADING(state, s) {
		state._isSearchHistoryLoading = !!s;
	},
	SET_SEARCH_HISTORY_FETCHED_AT(state, ts) {
		state._searchHistoryFetchedAt = ts || Date.now();
	}
};

export default {
	state,
	getters,
	actions,
	mutations
};
